import React, {useEffect, useMemo, useRef, useState} from 'react'
import {Elements, PaymentElement, useElements, useStripe} from '@stripe/react-stripe-js'
import {loadStripe, Stripe} from '@stripe/stripe-js'
import {StripePaiementSociete} from '../../../../components/requests'

/**
 * Paiement CB du panier.
 *
 * Chaque société (AUTO / MOTO / INDUSTRIE) possède son propre espace Stripe :
 * un panier réparti sur plusieurs sociétés génère plusieurs PaymentIntent, qui
 * doivent être réglés l'un après l'autre, chacun avec la clé publique de son
 * compte. La validation de la commande n'est déclenchée qu'une fois tous les
 * paiements encaissés.
 */

type Props = {
  paiements: StripePaiementSociete[]
  /** Appelé quand TOUTES les sociétés du panier sont payées. */
  onAllPaid: (paymentIntentIds: string[]) => void
  onError?: (message: string) => void
  /** Le bouton « Payer » du footer du modal déclenche cette fonction. */
  payerRef: React.MutableRefObject<(() => void) | null>
  onEtapeChange?: (etape: number, total: number) => void
  setEnCours?: (enCours: boolean) => void
  /** Prévient le modal que tout est encaissé (le bouton devient « Finaliser »). */
  onTousPayesChange?: (tousPayes: boolean) => void
}

// Un objet Stripe par clé publique : loadStripe met en cache par clé.
const cacheStripe = new Map<string, Promise<Stripe | null>>()
const getStripe = (publicKey: string): Promise<Stripe | null> => {
  if (!cacheStripe.has(publicKey)) {
    cacheStripe.set(
      publicKey,
      loadStripe(publicKey, {
        developerTools: {assistant: {enabled: process.env.NODE_ENV !== 'production'}},
      })
    )
  }
  return cacheStripe.get(publicKey) as Promise<Stripe | null>
}

const formatEuros = (montant: number): string =>
  new Intl.NumberFormat('fr-FR', {style: 'currency', currency: 'EUR'}).format(montant ?? 0)

/** Formulaire d'une société : porte le PaymentElement et la confirmation Stripe. */
const EtapePaiement = ({
  paiement,
  onPaye,
  onError,
  payerRef,
  setEnCours,
}: {
  paiement: StripePaiementSociete
  onPaye: (paymentIntentId: string) => void
  onError?: (message: string) => void
  payerRef: React.MutableRefObject<(() => void) | null>
  setEnCours?: (enCours: boolean) => void
}) => {
  const stripe = useStripe()
  const elements = useElements()
  const [erreur, setErreur] = useState<string | null>(null)
  const [traitement, setTraitement] = useState(false)

  const confirmer = async () => {
    if (!stripe || !elements || traitement) return

    setTraitement(true)
    setEnCours?.(true)
    setErreur(null)

    const {error: erreurValidation} = await elements.submit()
    if (erreurValidation) {
      const message = erreurValidation.message ?? 'Informations de paiement invalides.'
      setErreur(message)
      onError?.(message)
      setTraitement(false)
      setEnCours?.(false)
      return
    }

    const {error, paymentIntent} = await stripe.confirmPayment({
      elements,
      confirmParams: {return_url: window.location.href},
      redirect: 'if_required',
    })

    setTraitement(false)
    setEnCours?.(false)

    if (error) {
      const message = error.message ?? 'Le paiement a été refusé.'
      setErreur(message)
      onError?.(message)
      return
    }

    if (paymentIntent && ['succeeded', 'processing'].includes(paymentIntent.status)) {
      onPaye(paymentIntent.id)
      return
    }

    const message = "Le paiement n'a pas pu être finalisé."
    setErreur(message)
    onError?.(message)
  }

  // Le bouton de validation vit dans le footer du modal : on lui expose la confirmation.
  // Le nettoyage ne remet à null que SA propre fonction, pour ne pas écraser
  // celle que le parent installe une fois tous les paiements encaissés.
  useEffect(() => {
    payerRef.current = confirmer
    return () => {
      if (payerRef.current === confirmer) payerRef.current = null
    }
  })

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        confirmer()
      }}
    >
      <PaymentElement options={{wallets: {link: 'never'}}} />
      {erreur && (
        <div className={'alert alert-danger mt-3 py-2 px-3 mb-0 fs-7'} role='alert'>
          {erreur}
        </div>
      )}
      {traitement && (
        <div className={'text-muted fs-7 mt-3'}>
          <span className='spinner-border spinner-border-sm align-middle me-2'></span>
          Paiement en cours…
        </div>
      )}
      <button type={'submit'} className={'d-none'}></button>
    </form>
  )
}

const StripePaiementStepper = ({
  paiements,
  onAllPaid,
  onError,
  payerRef,
  onEtapeChange,
  setEnCours,
  onTousPayesChange,
}: Props) => {
  const [etape, setEtape] = useState(0)
  const [payes, setPayes] = useState<string[]>([])
  const dejaNotifie = useRef(false)
  const tousPayes = paiements.length > 0 && payes.length >= paiements.length

  // Un nouveau jeu de PaymentIntent (panier modifié) réinitialise le parcours.
  const signature = useMemo(() => paiements.map((p) => p.paymentIntentId).join('|'), [paiements])
  useEffect(() => {
    setEtape(0)
    setPayes([])
    dejaNotifie.current = false
  }, [signature])

  useEffect(() => {
    onEtapeChange?.(etape, paiements.length)
  }, [etape, paiements.length, onEtapeChange])

  useEffect(() => {
    onTousPayesChange?.(tousPayes)
  }, [tousPayes, onTousPayesChange])

  // Tout est encaissé mais la validation de la commande a échoué (réseau, 4xx…) :
  // il ne faut surtout pas reconfirmer les PaymentIntent, seulement rejouer la
  // validation côté Koena. Le bouton du footer pointe donc sur onAllPaid.
  useEffect(() => {
    if (!tousPayes) return

    const rejouerValidation = () => onAllPaid(payes)
    payerRef.current = rejouerValidation

    return () => {
      if (payerRef.current === rejouerValidation) payerRef.current = null
    }
  }, [tousPayes, payes, onAllPaid, payerRef])

  const enregistrerPaiement = (paymentIntentId: string) => {
    const misAJour = [...payes, paymentIntentId]
    setPayes(misAJour)

    if (misAJour.length >= paiements.length) {
      if (!dejaNotifie.current) {
        dejaNotifie.current = true
        onAllPaid(misAJour)
      }
      return
    }

    setEtape(etape + 1)
  }

  if (paiements.length === 0) return null

  const courant = paiements[Math.min(etape, paiements.length - 1)]
  const multiSociete = paiements.length > 1
  const totalPanier = paiements.reduce((total, p) => total + (p.montantTtc ?? 0), 0)

  return (
    <div>
      {multiSociete && (
        <div className={'mb-4'}>
          <div className={'alert alert-light-primary py-2 px-3 fs-7 mb-3'}>
            Votre panier concerne {paiements.length} sociétés : le règlement s'effectue en{' '}
            {paiements.length} paiements distincts, un par société.
          </div>
          <div className={'d-flex flex-column gap-2'}>
            {paiements.map((paiement, index) => {
              const estPaye = index < payes.length
              const estCourant = index === etape && !estPaye
              return (
                <div
                  key={paiement.paymentIntentId}
                  className={`d-flex align-items-center justify-content-between border rounded p-2 ${
                    estCourant ? 'border-primary' : ''
                  } ${estPaye ? 'bg-light-success' : ''}`}
                >
                  <div className={'d-flex align-items-center gap-2'}>
                    <span className={`badge ${estPaye ? 'badge-success' : 'badge-light'}`}>
                      {index + 1}/{paiements.length}
                    </span>
                    <span className={`fs-7 ${estCourant ? 'fw-bold text-gray-800' : 'text-muted'}`}>
                      {paiement.raisonSociale}
                    </span>
                  </div>
                  <div className={'d-flex align-items-center gap-3'}>
                    <span className={'fs-7 text-gray-700'}>{formatEuros(paiement.montantTtc)}</span>
                    {estPaye && <i className='ki-duotone ki-check-circle fs-4 text-success' />}
                  </div>
                </div>
              )
            })}
          </div>
          <div className={'d-flex justify-content-between mt-3 fs-6 fw-bold text-gray-800'}>
            <span>Total</span>
            <span>{formatEuros(totalPanier)}</span>
          </div>
          <hr />
        </div>
      )}

      {tousPayes ? (
        <div className={'alert alert-success py-3 px-3 mb-0 fs-7'} role='alert'>
          <div className={'fw-bold mb-1'}>
            Paiement encaissé — {formatEuros(totalPanier)}
            {multiSociete ? ` (${paiements.length} sociétés)` : ''}
          </div>
          Finalisation de la commande en cours. Si elle n'aboutit pas, relancez-la avec le bouton
          ci-dessous : aucun nouveau prélèvement ne sera effectué.
        </div>
      ) : (
        <>
          <div className={'d-flex justify-content-between align-items-center mb-3'}>
            <span className={'fs-6 text-gray-800'}>
              {multiSociete
                ? `Paiement ${Math.min(etape + 1, paiements.length)}/${paiements.length} — ${
                    courant.raisonSociale
                  }`
                : courant.raisonSociale}
            </span>
            <span className={'fs-6 fw-bold text-gray-800'}>{formatEuros(courant.montantTtc)}</span>
          </div>

          <Elements
            key={courant.paymentIntentId}
            stripe={getStripe(courant.publicKey)}
            options={{clientSecret: courant.clientSecret, locale: 'fr'}}
          >
            <EtapePaiement
              paiement={courant}
              onPaye={enregistrerPaiement}
              onError={onError}
              payerRef={payerRef}
              setEnCours={setEnCours}
            />
          </Elements>
        </>
      )}
    </div>
  )
}

export default StripePaiementStepper
